import { useMemo } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useGetUserKey } from '@/hooks/useQueryKeys';
import { GraphQuery, MyResourceKey, UniqueQueryKeys } from '@/types';
import { UseExtendQueryOptions, useQuery } from '@/utils/api';
import { sToMs } from '@/utils/date.util';

interface GroupedRow {
  fields: Record<string, unknown>;
  count: number;
}

interface GroupedResponse {
  groups: GroupedRow[];
  offset?: number;
}

/**
 * Loose GraphQuery type that accepts both the main types.ts GraphQuery
 * and the query builder's legacy variant (which uses `operator: string`
 * instead of the stricter `Operator` union type).
 *
 * Both produce identical JSON payloads at runtime — the difference is
 * purely in TypeScript's structural typing. This avoids forcing callers
 * to cast their query objects.
 */
type LooseGraphQuery =
  | GraphQuery
  | (Omit<GraphQuery, 'node'> & { node: object });

interface UseFilteredCountProps {
  resource: MyResourceKey;
  /** The active GraphQuery from the main data hook (same filters the table uses) */
  query: LooseGraphQuery | null | undefined;
}

interface UseFilteredCountResult {
  /** Total count of items matching the filters, undefined while loading */
  count: number | undefined;
  /** Whether the count query is currently loading */
  isLoading: boolean;
  /** Whether the count query errored */
  isError: boolean;
}

/**
 * Hook that fetches the total count of items matching the active filters
 * by sending a GroupBy query with the same filters as the main data query.
 *
 * Uses `groupBy: ["status"]` to get counts per status, then sums them.
 * This is lightweight since it only returns aggregated counts, not full data.
 */
export function useFilteredCount(
  { resource, query }: UseFilteredCountProps,
  options?: UseExtendQueryOptions<GroupedResponse> & { enabled?: boolean }
): UseFilteredCountResult {
  const axios = useAxios({});

  // Build the count query: same filters but with groupBy to get counts
  const countQuery = useMemo(() => {
    if (!query) return null;

    return {
      ...query,
      // Group by status to get count per status (we'll sum them)
      groupBy: ['status'],
      // We only need counts, fetch many groups in one page
      limit: 1000,
      page: 0,
      // Strip sorting — irrelevant for counts
      orderBy: undefined,
      descending: undefined,
      orderByFields: undefined,
    };
  }, [query]);

  const enabled = options?.enabled !== false && countQuery !== null;

  const result = useQuery<GroupedResponse>({
    ...options,
    defaultErrorMessage: `Failed to fetch ${resource} count`,
    retry: 1,
    staleTime: sToMs(30),
    refetchInterval: enabled ? sToMs(60) : false,
    refetchIntervalInBackground: false,
    queryKey: useGetUserKey(
      [
        UniqueQueryKeys.MY,
        'filtered-count',
        resource,
        JSON.stringify(countQuery),
      ],
      false
    ),
    queryFn: async () => {
      if (!countQuery) {
        return { groups: [] };
      }

      const { data } = await axios({
        method: 'POST',
        url: '/my',
        params: {
          label: resource,
        },
        data: countQuery,
      });

      return data as GroupedResponse;
    },
    enabled,
  });

  const count = useMemo(() => {
    if (!result.data?.groups) return undefined;
    return result.data.groups.reduce(
      (sum: number, group: GroupedRow) => sum + (group.count || 0),
      0
    );
  }, [result.data]);

  return {
    count,
    isLoading: result.isLoading,
    isError: result.isError,
  };
}
